import { createContext, useCallback, useContext, useState } from "react";
import { useAuth } from "@/auth/AuthContext";
import { CampaignService } from "@/services/CampaignService";
import { LeadService } from "@/services/LeadService";

const CAMPAIGNS_TO_SAMPLE = 5;
const LEADS_PER_CAMPAIGN_SAMPLE = 10;

const SalesDashboardDataContext = createContext(null);

// Mirrors RecruitmentDashboardDataContext: lives above the routes so the sales dashboard's
// data survives navigating away to /recruitment and back instead of re-fetching every switch.
export const SalesDashboardDataProvider = ({ children }) => {
    const { currentUser } = useAuth();
    const [state, setState] = useState({
        status: "idle", // idle -> loading -> loaded
        activeCampaigns: [],
        activeTotalCount: 0,
        sampledLeads: [],
    });

    const load = useCallback(() => {
        if (!currentUser?.id || state.status !== "idle") return;
        setState((prev) => ({ ...prev, status: "loading" }));

        CampaignService.getActiveByOwner(currentUser.id, 1, 20)
            .then(async (campaignsResponse) => {
                const items = campaignsResponse?.items ?? [];
                const sortedByRecent = [...items].sort(
                    (a, b) => new Date(b.updatedAtUtc) - new Date(a.updatedAtUtc)
                );
                const sample = sortedByRecent.slice(0, CAMPAIGNS_TO_SAMPLE);
                const leadResults = await Promise.allSettled(
                    sample.map((campaign) =>
                        LeadService.getByCampaign(campaign.id, 1, LEADS_PER_CAMPAIGN_SAMPLE, [], null, "createdat", "desc")
                            .then((res) => ({ campaign, res }))
                    )
                );

                const campaignsWithCounts = sample.map((campaign, index) => {
                    const result = leadResults[index];
                    const totalCount = result?.status === "fulfilled" ? result.value.res?.totalCount ?? null : null;
                    return { ...campaign, leadCount: totalCount };
                });

                const flatLeads = leadResults
                    .filter((result) => result.status === "fulfilled")
                    .flatMap(({ value: { campaign, res } }) =>
                        (res?.items ?? []).map((lead) => ({ lead, campaign }))
                    );

                setState({
                    status: "loaded",
                    activeCampaigns: campaignsWithCounts,
                    activeTotalCount: campaignsResponse?.totalCount ?? items.length,
                    sampledLeads: flatLeads,
                });
            })
            .catch(() => setState((prev) => ({ ...prev, status: "loaded" })));
    }, [currentUser?.id, state.status]);

    const value = {
        ...state,
        isLoading: state.status !== "loaded",
        load,
    };

    return (
        <SalesDashboardDataContext.Provider value={value}>
            {children}
        </SalesDashboardDataContext.Provider>
    );
};

export const useSalesDashboardData = () => useContext(SalesDashboardDataContext);
